import { CommonModule } from '@angular/common';
import { Component, OnInit, QueryList, ViewChildren, booleanAttribute, inject, input, model, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, take } from 'rxjs';
import { CollapsibleComponent } from '../collapsible/collapsible.component';
import { areListsEqual } from '../common/utils';
import { TechsStore } from '../core/stores/techs.store';
import { Ecosystem, Tag, TagGenre } from '../models/models';
import { TechService } from '../services/tech.service';
import { TagComponent } from '../tag/tag.component';

@Component({
  standalone: true,
  imports: [CommonModule, CollapsibleComponent, TagComponent, FormsModule],
  selector: 'app-tag-selector',
  templateUrl: './tag-selector.component.html',
  styleUrls: ['./tag-selector.component.css'],
})
export class TagSelectorComponent implements OnInit {
  private techService = inject(TechService);
  private toastr = inject(ToastrService);

  readonly techsStore = inject(TechsStore);

  hideSearch = input(false, { transform: booleanAttribute })
  hideExclude = input(false, { transform: booleanAttribute })
  saveButton = input(false, { transform: booleanAttribute })
  label = input("Filter")
  saveLabel = input("Save")
  initialTagIds = input<string[] | undefined | null>()
  initialEcosystemIds = input<string[] | undefined | null>()


  initialTagIds$ = toObservable(this.initialTagIds)
  initialEcosystemIds$ = toObservable(this.initialEcosystemIds)

  search = model<string | undefined>()
  allTags = signal<Tag[]>([]);
  allGenres = signal<TagGenre[]>([]);
  allEcosystems = signal<Ecosystem[]>([]);
  toggleCollapseStatus = signal(false);
  tagsChanged = output<Tag[]>()
  ecosystemsChanged = output<Ecosystem[]>()
  onSave = output<{ tags: Tag[], ecosystems: Ecosystem[] }>()

  @ViewChildren(CollapsibleComponent) collapsibles!: QueryList<CollapsibleComponent>;

  DEFAULT_BACKGROUNDCOLOR: string = '#6C757D';
  DEFAULT_TEXTCOLOR: string = '#FFFFFF';
  SELECTED_OPACITY: number = 50;
  UNSELECTED_OPACITY: number = 100;


  ngOnInit(): void {
    combineLatest({
      tags: this.techService.getTagsWithDetail().pipe(take(1)),
      genres: this.techService.getTagGenres().pipe(take(1)),
      ecosystems: this.techService.getEcosystems().pipe(take(1)),
      initialTagIds: this.initialTagIds$,
      initialEcosystemIds: this.initialEcosystemIds$,
    }).subscribe({
      next: ({ tags, genres, ecosystems, initialTagIds, initialEcosystemIds }) => {

        this.allGenres.set(genres.data || []);
        this.allTags.set(tags.data?.map(tag => ({
          ...tag,
          selected: initialTagIds?.includes(tag.id!)
        })) || []
        );


        this.allEcosystems.set(ecosystems.data?.map(ecosystem => ({
          ...ecosystem,
          selected: initialEcosystemIds?.includes(ecosystem.id!),
          backgroundColor: ecosystem.backgroundColor || this.DEFAULT_BACKGROUNDCOLOR,
          textColor: ecosystem.textColor || this.DEFAULT_TEXTCOLOR
        })) || [])
      },
      error: (error) => {
        console.error('Error loading data', error);
      }
    });
  }

  toast(message: string, title: string) {
    this.toastr.error(message, title, { timeOut: 5000, });
  }

  isTag(item: Tag | Ecosystem): boolean {
    if (item && 'tagGenre' in item) {
      return true;
    } else if (item && 'backgroundColor' in item) {
      return false
    } else {
      this.toast("Invalid datatype.", "Something went wrong.");
      return false
    }
  }

  toggleCollapse() {
    this.toggleCollapseStatus.update(prev => !prev);
    if (!this.toggleCollapseStatus()) {
      this.expandAll();
    } else {
      this.collapseAll();
    }
  }

  collapseAll() {
    this.collapsibles.forEach(collapsible => collapsible.collapse());
  }

  expandAll() {
    this.collapsibles.forEach(collapsible => collapsible.expand());
  }

  resetFilters() {
    this.search.set('');

    this.expandAll();
    this.toggleCollapseStatus.set(false);

    this.allTags.update(tags => tags.map(tag => ({ ...tag, selected: false })))
    this.allEcosystems.update(ecosystems => ecosystems.map(ecosystem => ({ ...ecosystem, selected: false })))
    this.tagsChanged.emit(this.getSelectedTags());
    this.ecosystemsChanged.emit(this.getSelectedEcosystems());
    if (!this.hideExclude()) {
      this.techsStore.resetExclude()
    }

  }
  getSelectedTags() {
    return this.getSortedTags()?.filter(tag => tag.selected);
  }

  getSelectedEcosystems() {
    return this.getSortedEcosystems()?.filter(ecosystem => ecosystem.selected);
  }
  getSortedEcosystems() {
    return this.allEcosystems().toSorted((a, b) => a.name.localeCompare(b.name))
  }
  getSortedTags() {
    return this.allTags().toSorted((a, b) => a.name.localeCompare(b.name))
  }
  filterTagByGenre(genreId: string | undefined): Tag[] | undefined {
    return this.getSortedTags()?.filter(tag => tag.tagGenre?.id === genreId);
  }

  filterTagWithoutGenre(): Tag[] | undefined {
    return this.getSortedTags()?.filter(tag => tag.tagGenre === null);
  }
  selectedTagsChanged(): boolean {
    const selectedEcoIds = Array.from(new Set(this.getSelectedEcosystems().map(e => e.id)))
    const initialEcoIds = Array.from(new Set(this.initialEcosystemIds()))
    const selectedTagIds = Array.from(new Set(this.getSelectedTags().map(t => t.id)))
    const initialTagIds = Array.from(new Set(this.initialTagIds()))

    return !areListsEqual(initialEcoIds, selectedEcoIds) || !areListsEqual(initialTagIds, selectedTagIds)

  }

  toggleTag(tag: Tag) {
    this.allTags.update(prevTags => {
      const foundTag = prevTags.find(t => t.id === tag.id)
      const otherTags = prevTags.filter(t => t.id !== tag.id)
      if (!foundTag) return otherTags
      foundTag.selected = !(foundTag?.selected)
      return [...otherTags, foundTag]
    })
    this.tagsChanged.emit(this.getSelectedTags())
  }
  toggleEcosystem(ecosystem: Ecosystem) {

    this.allEcosystems.update(prevEcosystem => {
      const foundEcosystem = prevEcosystem.find(e => e.id === ecosystem.id)
      const otherEcosystems = prevEcosystem.filter(e => e.id !== ecosystem.id)
      if (!foundEcosystem) return otherEcosystems
      foundEcosystem.selected = !(foundEcosystem?.selected)
      return [...otherEcosystems, foundEcosystem]
    })
    this.ecosystemsChanged.emit(this.getSelectedEcosystems())
  }
  onSaveClick() {
    this.onSave.emit({
      tags: this.getSelectedTags(),
      ecosystems: this.getSelectedEcosystems()
    })
  }
  excludeEcosystemButtonStyle(): string {
    return this.techsStore.excludeEcosystem() ? 'danger' : 'outline-primary';
  }
  excludeGenreButtonStyle(genre: string): string {
    return this.techsStore.excludedGenres().includes(genre) ? 'danger' : 'outline-primary';
  }

}
